// src/components/CampaignWizard/CampaignWizardModal.tsx
import React, { useEffect, useMemo, useState } from "react";
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Row,
    Col,
    Card,
    CardBody,
    Button,
    Badge,
    Alert,
    Spinner
} from "reactstrap";
import { withTranslation, WithTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { ApplicationState } from "store";
import {
    campaignCreateRequest,
    campaignGetRequest,
    campaignUpdateRequest,
    campaignScheduleRequest,
} from "store/campaigns/actions";

import StepBasicInfo from "./StepBasicInfo";
import StepChannels, { ChannelType, ChannelState } from "./StepChannels";
import { AudienceData } from "store/campaigns/types";
import StepSchedule from "./StepSchedule";
import StepFallback, { FallbackRule } from "./StepFallback";
import StepCost from "./StepCost";
import StepPreview from "./StepPreview";
import StepReview from "./StepReview";
import SummarySidebar, { SummaryItem } from "./SummarySidebar";
import _ from "lodash";
import { isJson } from "utils";
import { studentsMetadataGetRequest } from 'store/student/action';
import axios from "axios";
import config from "../../../config";
import StepAudience from "./StepAudience";

const { api } = config;
const BASE = api.DTS_API_CAMPAIGN;

type BasicInfo = {
    name: string;
    description: string;
    campaign_type: "MARKETING" | "TRANSACTIONAL";
};

type Schedule = {
    immediate: boolean;
    scheduledTime: Date | null;
    localTime: boolean;
    recurrence: any | null;
};

type CostEstimate = {
    emailCost: number;
    smsCost: number;
    inAppCost: number;
    totalCost: number;
};

type PreviewData = {
    emailHtml: string;
    smsText: string;
    inApp: { title: string; body: string };
};

const STEPS = [
    { icon: "ri-edit-box-line", labelKey: "Basic Info" },
    { icon: "ri-mail-send-line", labelKey: "Channels & Templates" },
    { icon: "ri-group-line", labelKey: "Audience" },
    { icon: "ri-calendar-line", labelKey: "Schedule" },
    { icon: "ri-repeat-line", labelKey: "Fallback Rules" },
    { icon: "ri-price-tag-3-line", labelKey: "Cost Estimate" },
    { icon: "ri-eye-line", labelKey: "Preview" },
    { icon: "ri-check-double-line", labelKey: "Review & Submit" },
] as const;

const CampaignWizardModal: React.FC<WithTranslation> = ({ t }) => {
    const dispatch = useDispatch<any>();
    const navigate = useNavigate();
    const { id } = useParams<{ id?: string }>();
    const token = useSelector((s: ApplicationState) => s.auth.token);
    const existing = useSelector((s: ApplicationState) => s.campaign.current);
    const metaDataFields = useSelector((state: ApplicationState) => state.student.metaDataFields);
    const installations = useSelector((s: ApplicationState) => s.integration.installations);
    const templates = useSelector((s: ApplicationState) => s.templates.list);
    const loading = useSelector((s: ApplicationState) => s.campaign.loading);
    const error = useSelector((s: ApplicationState) => s.campaign.error);


    useEffect(() => {
        dispatch(studentsMetadataGetRequest("students", { pageSize: 500 }));
    }, []);

    const [isOpen, setIsOpen] = useState(true);
    const close = () => {
        setIsOpen(false);
        navigate("/campaigns");
    };

    const [step, setStep] = useState(0);

    // ── wizard state ────────────────────────────
    const [basicInfo, setBasicInfo] = useState<BasicInfo>({
        name: "",
        description: "",
        campaign_type: "MARKETING",
    });

    const [channelState, setChannelState] = useState<ChannelState>({
        channels: { EMAIL: false, WHATSAPP: false, SMS: false, IN_APP: false },
        selectedInstallByChannel: {},
        templateByChannel: {},
        fromByChannel: {},
    });
    // 3) Audience: use our new AudienceDraft
    const [audience, setAudience] = useState<AudienceData>({
        type: "STATIC",
        file: null,
        document_key: "",
        columns: [],
        mappings: [],
        filter_groups: [],
        imported_count: 0,
        estimated_count: 0
    });
    const [audienceSnapshotMode, setAudienceSnapshotMode] = useState<"STATIC" | "DYNAMIC">("DYNAMIC");
    const [audienceUploading, setAudienceUploading] = useState(false);



    const [schedule, setSchedule] = useState<Schedule>({
        immediate: true,
        scheduledTime: null,
        localTime: false,
        recurrence: null,
    });

    const [fallbackRules, setFallbackRules] = useState<FallbackRule[]>([]);
    const [cost, setCost] = useState<CostEstimate>({
        emailCost: 0,
        smsCost: 0,
        inAppCost: 0,
        totalCost: 0,
    });

    const [preview, setPreview] = useState<PreviewData>({
        emailHtml: "",
        smsText: "",
        inApp: { title: "", body: "" },
    });
    const [localError, setLocalError] = useState<string | null>(null);


    // ── load existing campaign on edit ──────────
    useEffect(() => {
        if (id) dispatch(campaignGetRequest(id));
    }, [id, dispatch]);

    useEffect(() => {
        if (!existing) return;

        setBasicInfo({
            name: existing.name,
            description: existing.description || "",
            campaign_type: existing.campaign_type as any,
        });

        setChannelState({
            channels: {
                EMAIL: existing?.channels?.includes("EMAIL"),
                WHATSAPP: existing?.channels?.includes("WHATSAPP"),
                SMS: existing?.channels?.includes("SMS"),
                IN_APP: existing?.channels?.includes("IN_APP"),
            },
            selectedInstallByChannel: {
                EMAIL: (existing as any).emailInstallId,
                WHATSAPP: (existing as any).whatsappInstallId,
                SMS: (existing as any).smsInstallId,
                IN_APP: (existing as any).inAppInstallId,
            },
            templateByChannel: {
                EMAIL: (existing as any).emailTemplateId,
                WHATSAPP: (existing as any).whatsappTemplateId,
                SMS: (existing as any).smsTemplateId,
                IN_APP: (existing as any).inAppTemplateId,
            },
            fromByChannel: {
                EMAIL: (existing as any).email,
                WHATSAPP: (existing as any).email,
                SMS: (existing as any).email,
                IN_APP: (existing as any).email,
            },
        });

        if (existing.audience) {
            const a = existing.audience;
            setAudience({
                type: a.type,
                file: null,
                document_key: a.document_key || '',
                columns: a.columns || [],
                mappings: a.mappings || [],
                imported_count: a.imported_count || 0,
                estimated_count: a.estimated_count || 0,
                filter_groups: a.filter_groups || [],
            });
        }

        setAudienceSnapshotMode(existing.audience_snapshot_mode || "DYNAMIC");

        // TODO: hydrate `audience`, `schedule`, `fallbackRules`
    }, [existing]);

    // metaDataFields from redux or prop:
    const columns = useMemo(() => {
        return metaDataFields?.map((fieldName) => {
            let fields = isJson(fieldName.valuesJson)
                ? JSON.parse(fieldName.valuesJson)
                : (fieldName?.valuesJson || {});
            return {
                Header: _.startCase(fields.label),
                accessor: fields.key,
                id: fields.key,
                fieldid: fieldName.id,
                filterable: true,
                isSortable: false,
                disableFilters: true,
                valueJson: fields,
                meta: fieldName,
                show: fields.tablecolumn,
                isPrimary: fields.isPrimary,
            };
        }) || [];
    }, [metaDataFields]);

    // ── per-step validation ─────────────────────
    const canNext = () => {
        switch (step) {
            case 0:
                return !!basicInfo.name.trim();
            case 1:
                // return Object.entries(channelState.channels).some(
                //   ([ch, on]) => on && !!channelState.templateByChannel[ch as ChannelType]
                // );
                return true
            case 2:
                // return audience.type === "STATIC"
                //     ? !!audience.csvFile
                //     : !!(audience as any).filterJson;

                return true
            case 3:
                return schedule.immediate || !!schedule.scheduledTime;
            default:
                return true;
        }
    };
    const onNext = async () => {
        setLocalError(null);
        if (step === 2 && audience.type === "STATIC") {
            try {
                setAudienceUploading(true);
                const data = await axios.post(
                    `${BASE}/api/v1/imports/process`,
                    {
                        document_id: audience.document_key,
                        mappings: audience.mappings.filter(m => m.crm_field)
                    },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                // capture how many rows got imported
                const { imported_count } = data as any;
                setAudience(a => ({ ...a, imported_count: imported_count }));
            } catch (err) {
                console.error("Import failed", err);
                setLocalError("Import failed. Please check your file and try again.");
                setAudienceUploading(false);
                return; // don’t advance if the API errored
            }
            setAudienceUploading(false);
        }
        if (step < STEPS.length - 1 && canNext()) setStep((s) => s + 1);
    };
    const onBack = () => {
        if (step > 0) setStep((s) => s - 1);
    };

    const onFinish = async () => {
        setLocalError(null);
        try {

            // When building backend payload:
            const enabledChannels = (Object.keys(channelState.channels) as ChannelType[])
                .filter((ch) => channelState.channels[ch]);

            const channelsPayload = enabledChannels.map((ch) => {
                // Find install from the available installs (in memory at the time of save)
                const install = installations.find((i) => i.id === channelState.selectedInstallByChannel[ch]);
                const template = templates?.find((t) => t.id === channelState.templateByChannel[ch]);

                return {
                    channel: ch,
                    install_id: channelState.selectedInstallByChannel[ch] || null,
                    install_summary: install
                        ? {
                            logo: install.version?.metadata?.logo || "",
                            app_name: install.version?.metadata?.name || "",
                            account_name:
                                install.name ||
                                (() => {
                                    try {
                                        const c = JSON.parse(install.config_json || "{}");
                                        return c.domain || c.email || c.sender || install.id;
                                    } catch {
                                        return install.id;
                                    }
                                })()
                        }
                        : null,
                    template_id: channelState.templateByChannel[ch] || null,
                    template_summary: template
                        ? {
                            name: (() => {
                                try {
                                    return template.valuesJson?.name || template.id;
                                } catch {
                                    return template.id;
                                }
                            })(),
                            id: template.id
                        }
                        : null,
                    template_content: template?.valuesJson || null,
                    sender_address: channelState.fromByChannel[ch] || null
                };
            });

            // 1) build the “create” payload exactly in snake_case
            const payload: any = {
                name: basicInfo.name,
                description: basicInfo.description,
                campaign_type: basicInfo.campaign_type,        // MARKETING | TRANSACTIONAL
                channels: channelsPayload,
                templates: Object.fromEntries(
                    Object.entries(channelState.templateByChannel)
                        .filter(([ch, tpl]) => channelState.channels[ch as ChannelType] && tpl)
                ),

                schedule: {
                    immediate: schedule.immediate,
                    scheduled_time_utc: schedule.immediate
                        ? null
                        : schedule.scheduledTime?.toISOString(),
                    send_in_local_time: schedule.localTime,
                    recurrence_rule: schedule.recurrence          // e.g. { freq:"WEEKLY", … }
                },

                audience: {
                    type: audience.type,
                    csv_file_name: audience.document_key /* or if you want the filename itself */,
                    filter_groups: audience.filter_groups,

                    // UI-only, to satisfy your extended DTO:
                    document_key: audience.document_key,
                    columns: audience.columns,
                    mappings: audience.mappings.filter(m => m.crm_field),
                    imported_count: audience.imported_count,
                },
                audience_snapshot_mode: audienceSnapshotMode,

                fallback_rules: fallbackRules.map(r => ({
                    from_channel: r.fromChannel,
                    on_events: r.onEvents,
                    to_channel: r.toChannel,
                    delay_hours: r.delayHours
                }))
            };

            // 2) create
            await dispatch(campaignCreateRequest(payload));
            close();
        } catch (err) {
            setLocalError("Campaign creation failed. Please check your details and try again.");
        }
    };



    // ── stepper UI ─────────────────────────────
    const renderStepper = () => (
        <div className="px-4 py-3">
            <div className="d-flex justify-content-between align-items-start gap-4">
                {STEPS.map((s, i) => {
                    const completed = i < step;
                    const active = i === step;
                    const circleClass = completed
                        ? "bg-success text-white"
                        : active
                            ? "bg-primary text-white"
                            : "bg-white border border-secondary text-secondary";

                    return (
                        <div
                            key={i}
                            className="text-center flex-fill d-flex flex-column align-items-center"
                            style={{ cursor: completed ? "pointer" : undefined }}
                            onClick={() => completed && setStep(i)}
                        >
                            <div
                                className={`rounded-circle shadow-sm d-flex align-items-center justify-content-center ${circleClass}`}
                                style={{ width: 48, height: 48, fontSize: 20, transition: "all .2s" }}
                            >
                                {completed ? <i className="ri-check-line" /> : <i className={s.icon} />}
                            </div>
                            <div
                                className={`mt-2 small ${completed ? "text-success" : active ? "text-primary fw-semibold" : "text-muted"
                                    }`}
                                style={{ fontSize: "0.85rem" }}
                            >
                                {t(s.labelKey)}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );

    const enabledChannels = (Object.keys(channelState.channels) as ChannelType[])
        .filter((ch) => channelState.channels[ch])
        .map((ch) => {
            const install = installations.find(i => i.id === channelState.selectedInstallByChannel[ch]);
            const template = templates?.find(t => t.id === channelState.templateByChannel[ch]);
            return {
                channel: ch,
                install_summary: install
                    ? {
                        logo: install.version?.metadata?.logo || "",
                        app_name: install.version?.metadata?.name || "",
                        account_name:
                            install.name ||
                            (() => {
                                try {
                                    const c = JSON.parse(install.config_json || "{}");
                                    return c.domain || c.email || c.sender || install.id;
                                } catch {
                                    return install.id;
                                }
                            })()
                    }
                    : null,
                template_summary: template
                    ? {
                        name: (() => {
                            try {
                                return template.valuesJson?.name || template.id;
                            } catch {
                                return template.id;
                            }
                        })(),
                        id: template.id
                    }
                    : null
            };
        });

    // ── summary sidebar ─────────────────────────
    const summary: SummaryItem[] = useMemo(() => {
        const audienceDetail =
            audience.type === "STATIC"
                ? `${t("CSV")} (${audience.imported_count ?? 0} ${t("rows")})`
                : `${t("Filter")} (${audience.filter_groups.length} ${t("groups")})`;
        return [
            {
                icon: "ri-edit-line",
                title: t("Basic Info"),
                detail: basicInfo.name || <span className="text-muted"><i className="ri-information-line" /> {t("Not set")}</span>
            },
            {
                icon: "ri-mail-send-line",
                title: t("Channels"),
                detail: enabledChannels.length > 0
                    ? enabledChannels.map((c: any) => (
                        <span key={c.channel} className="d-inline-flex align-items-center me-3 mb-2">
                            {c.install_summary?.logo && (
                                <img src={c.install_summary.logo} alt="" width={16} height={16} className="me-1" />
                            )}
                            <span>{c.install_summary?.app_name || c.channel}</span>
                            <span className="text-muted small ms-1">{c.install_summary?.account_name}</span>
                            {c.template_summary?.name && (
                                <Badge color="info" className="ms-1">{c.template_summary.name}</Badge>
                            )}
                        </span>
                    ))
                    : t("None"),
            },

            {
                icon: "ri-group-line",
                title: t("Audience"),
                detail: audienceDetail,
                badgeText: audience.type === "STATIC" && audience.file?.name ? audience.file?.name : 'None',
                badgeColor: audience.type === "STATIC" ? "info" : "secondary"
            },
            {
                icon: "ri-calendar-line",
                title: t("Schedule"),
                detail: schedule.immediate
                    ? t("Immediate")
                    : schedule.scheduledTime
                        ? `${schedule.scheduledTime.toLocaleString()}${schedule.localTime ? ` (${t("Local")})` : ""}`
                        : t("None"),
            },
            {
                icon: "ri-repeat-line",
                title: t("Fallback Rules"),
                detail: fallbackRules.length ? `${fallbackRules.length} ${t("rule", { count: fallbackRules.length })}` : t("None"),
            },
            {
                icon: "ri-price-tag-3-line",
                title: t("Cost Estimate"),
                detail: `$${cost.totalCost.toFixed(2)}`,
            },
        ];
    }, [basicInfo, channelState, audience, schedule, fallbackRules, cost, t]);


    return (
        <Modal isOpen={isOpen} fullscreen toggle={close} className="modal-premium">
            <ModalHeader
                toggle={close}
                className="bg-white border-bottom px-4 py-3 shadow-sm d-flex align-items-center"
                style={{ fontSize: "1.1rem", fontWeight: 600 }}
            >
                {id ? t("Edit Campaign") : t("New Campaign")}
            </ModalHeader>

            <ModalBody className="px-4 py-3 bg-light">
                {renderStepper()}
                {/* Show API or local error, premium style */}
                {(error || localError) && (
                    <Alert
                        color="danger"
                        toggle={() => { setLocalError(null); /* Optional: clear global error here if you want */ }}
                        fade
                        className="mb-3 rounded-3 shadow-sm d-flex align-items-center gap-2"
                    >
                        <i className="ri-error-warning-line fs-4 me-2 text-danger" />
                        <div>{error || localError}</div>
                    </Alert>
                )}

                <Row className="gx-4">
                    <Col lg={8}>
                        <Card className="shadow-sm border-light-subtle rounded-4 p-4 bg-white">
                            <CardBody className="p-0">
                                {step === 0 && (
                                    <StepBasicInfo data={basicInfo} onChange={setBasicInfo} />
                                )}
                                {step === 1 && (
                                    <StepChannels
                                        data={channelState}
                                        onChange={patch => setChannelState(prev => ({ ...prev, ...patch }))}
                                    />
                                )}
                                {step === 2 && (
                                    <StepAudience
                                        audience={audience}
                                        dataFields={columns}    // your metadata → { id, Header }
                                        t={t}
                                        onChange={setAudience}
                                        audienceSnapshotMode={audienceSnapshotMode}
                                        setAudienceSnapshotMode={setAudienceSnapshotMode}
                                    />
                                )}
                                {step === 3 && (
                                    <StepSchedule
                                        data={schedule}
                                        onChange={setSchedule}
                                    />
                                )}
                                {step === 4 && (
                                    <StepFallback
                                        data={fallbackRules}
                                        onChange={setFallbackRules}
                                    />
                                )}
                                {step === 5 && (
                                    <StepCost
                                        data={cost}
                                        setData={setCost}
                                        channels={enabledChannels.map(c => c.channel)} // ["EMAIL", "SMS", ...]
                                        audience={audience}
                                    />
                                )}
                                {step === 6 && (
                                    <StepPreview
                                        data={preview}
                                        channels={enabledChannels}
                                        setData={setPreview}
                                        templates={templates}
                                    />
                                )}
                                {step === 7 && (
                                    <StepReview
                                        basicInfo={basicInfo}
                                        channels={enabledChannels} // NOTE: Pass the array!
                                        audience={audience}
                                        schedule={schedule}
                                        fallbackRules={fallbackRules}
                                        costEstimate={cost}
                                        previewData={preview}
                                        templates={templates}
                                    />
                                )}
                            </CardBody>
                        </Card>
                    </Col>

                    <Col lg={4}>
                        <SummarySidebar items={summary} topOffset={80} />
                    </Col>
                </Row>
            </ModalBody>

            <ModalFooter className="bg-white border-top shadow-sm px-4 py-3 d-flex justify-content-between">
                <Button color="outline-secondary" disabled={step === 0 || loading} onClick={onBack}>
                    <i className="ri-arrow-left-line me-1" /> {t("Back")}
                </Button>
                {step < STEPS.length - 1 ? (
                    <Button color="primary" disabled={!canNext() || loading || (step === 2 && audienceUploading)} onClick={onNext}>
                        {loading || (step === 2 && audienceUploading) ? <Spinner size="sm" className="me-2" /> : null}
                        {t("Next")} <i className="ri-arrow-right-line ms-1" />
                    </Button>
                ) : (
                    <Button color="success" onClick={onFinish} disabled={loading}>
                        {loading && <Spinner size="sm" className="me-2" />}
                        {id ? t("Update & Schedule") : t("Create & Schedule")}
                    </Button>
                )}
            </ModalFooter>
        </Modal>
    );
};

export default withTranslation()(CampaignWizardModal);
